import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle2, AlertTriangle } from 'lucide-react';
import { groupLogsByExercise } from '@shared/logic/performance-logic';

interface PerformanceSetRowProps {
    log: any;
    index: number;
    onUpdate: (logId: string, field: string, value: any) => void;
    gridStyle: React.CSSProperties;
    inputStyle: React.CSSProperties;
}

const PerformanceSetRow: React.FC<PerformanceSetRowProps> = ({ log, index, onUpdate, gridStyle, inputStyle }) => {
    const [localWeight, setLocalWeight] = useState(log.weight ?? "");
    const [localReps, setLocalReps] = useState(log.reps ?? "");
    const [localRpe, setLocalRpe] = useState(log.rpe ?? "");

    useEffect(() => {
        setLocalWeight(log.weight ?? "");
        setLocalReps(log.reps ?? "");
        setLocalRpe(log.rpe ?? "");
    }, [log.weight, log.reps, log.rpe]);

    return (
        <div style={gridStyle}>
            <input 
                type="text" inputMode="decimal"
                value={localWeight}
                onChange={(e) => setLocalWeight(e.target.value)}
                onBlur={() => onUpdate(log.id, 'weight', localWeight)}
                style={inputStyle}
            />
            <input 
                type="text" inputMode="decimal"
                value={localReps}
                onChange={(e) => setLocalReps(e.target.value)}
                onBlur={() => onUpdate(log.id, 'reps', localReps)}
                style={inputStyle}
            />
            <input 
                type="text" inputMode="decimal"
                value={localRpe}
                onChange={(e) => setLocalRpe(e.target.value)}
                onBlur={() => onUpdate(log.id, 'rpe', localRpe)}
                style={{ ...inputStyle, color: '#2ecc71' }}
            />
            <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: '900', color: '#444' }}>{index + 1}</div>
        </div>
    );
};

interface CompletedWorkoutStateProps {
    performanceLogs: any[];
    updatePerformanceLog: (logId: string, field: string, value: any) => void;
}

export const CompletedWorkoutState: React.FC<CompletedWorkoutStateProps> = ({ performanceLogs, updatePerformanceLog }) => {
    const groupedLogs = groupLogsByExercise(performanceLogs);
    const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 40px', gap: '6px', alignItems: 'center', marginBottom: '8px' };
    const inputStyle: React.CSSProperties = { all: 'unset', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid #222', borderRadius: '6px', height: '32px', textAlign: 'center', fontSize: '14px', fontWeight: '900', color: '#fff', width: '100%' };

    return (
        <div style={{ paddingBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #111', paddingBottom: '10px' }}>
                <CheckCircle2 size={14} color="#2ecc71" />
                <span style={{ fontSize: '10px', fontWeight: '900', color: '#fff', letterSpacing: '2px' }}>PERFORMANCE DATA</span>
            </div>

            {groupedLogs.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', border: '1px dashed #222', borderRadius: '12px' }}>
                    <AlertTriangle size={24} color="#444" style={{ margin: '0 auto 10px' }} />
                    <p style={{ fontSize: '11px', color: '#666' }}>No exercise logs found for this session.</p>
                </div>
            ) : (
                groupedLogs.map((group, gi) => (
                    <div key={gi} style={{ marginBottom: '30px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '900', color: '#f29b11', textTransform: 'uppercase', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Activity size={14} /> {group.name}
                        </div>

                        <div style={{ 
                            ...gridStyle, marginBottom: '4px', opacity: 0.6, 
                            position: 'sticky', top: '0', backgroundColor: '#000', zIndex: 10, padding: '10px 0'
                        }}>
                            <div style={{ fontSize: '8px', fontWeight: '900', textAlign: 'center', color: '#666' }}>WEIGHT</div>
                            <div style={{ fontSize: '8px', fontWeight: '900', textAlign: 'center', color: '#666' }}>REPS</div>
                            <div style={{ fontSize: '8px', fontWeight: '900', textAlign: 'center', color: '#666' }}>RPE</div>
                            <div style={{ fontSize: '8px', fontWeight: '900', textAlign: 'center', color: '#666' }}>SET</div>
                        </div>

                        {group.sets.map((log, si) => (
                            <PerformanceSetRow 
                                key={log.id} 
                                log={log} 
                                index={si} 
                                onUpdate={updatePerformanceLog}
                                gridStyle={gridStyle}
                                inputStyle={inputStyle}
                            />
                        ))}
                    </div>
                ))
            )}
        </div>
    );
};
